import {
  dequeueAll,
  removeMutation,
  getQueueLength,
  getTempIdMap,
  setTempIdMapping,
  clearTempIdMap,
  QueuedMutation,
  clearCacheByPrefix,
} from './offlineStore';
import { contactsApi, interactionsApi, authApi } from './api';

// --- Network Utilities ---

export function isNetworkError(err: unknown): boolean {
  if (!navigator.onLine) return true;
  // fetch() throws TypeError on network failure
  if (err instanceof TypeError && err.message.includes('fetch')) return true;
  return false;
}

// --- Queue Replay ---

let replayInProgress = false;

interface ReplayCallbacks {
  refreshContacts: () => Promise<void>;
  refreshInteractions: (contactId?: string) => Promise<void>;
  onQueueLengthChange: (length: number) => void;
}

export async function replayQueue(callbacks: ReplayCallbacks): Promise<void> {
  if (replayInProgress) return;
  if (!navigator.onLine) return;

  const items = await dequeueAll();
  if (items.length === 0) return;

  replayInProgress = true;
  const tempIdMap = await getTempIdMap();
  let replayed = 0;

  try {
    for (const item of items) {
      // Check if we're still online
      if (!navigator.onLine) break;

      // Check dependency resolution
      if (item.dependsOnTempId && !tempIdMap[item.dependsOnTempId]) {
        // Dependency not yet resolved — skip for now (will be retried on next pass)
        continue;
      }

      try {
        await replayMutation(item, tempIdMap);
        await removeMutation(item.id!);
        replayed++;
        callbacks.onQueueLengthChange(await getQueueLength());
      } catch (err) {
        if (isNetworkError(err)) {
          // Lost connection mid-replay — stop
          break;
        }
        // 4xx error — bad data, remove from queue
        await removeMutation(item.id!);
        replayed++;
        callbacks.onQueueLengthChange(await getQueueLength());
      }
    }

    // If we replayed anything, refresh data from server
    if (replayed > 0) {
      // Clear stale caches so fresh data gets written
      await clearCacheByPrefix('contacts');
      await clearCacheByPrefix('interactions');
      await clearCacheByPrefix('dashboard');
      await clearCacheByPrefix('activity');

      await Promise.all([callbacks.refreshContacts(), callbacks.refreshInteractions()]);
    }

    // If queue is empty, clean up temp ID map
    const remaining = await getQueueLength();
    if (remaining === 0) {
      await clearTempIdMap();
    }
  } finally {
    replayInProgress = false;
  }
}

// Replay a single mutation against the API
async function replayMutation(
  item: QueuedMutation,
  tempIdMap: Record<string, number>,
): Promise<void> {
  const payload = item.payload as Record<string, unknown>;

  switch (item.type) {
    case 'addContact': {
      const apiContact = await contactsApi.create(
        payload as Parameters<typeof contactsApi.create>[0],
      );
      // Store temp → server ID mapping
      if (item.tempId) {
        tempIdMap[item.tempId] = apiContact.id;
        await setTempIdMapping(item.tempId, apiContact.id);
      }
      break;
    }

    case 'updateContact': {
      let contactId = payload.id as number;
      const tempKey = String(contactId);
      if (tempKey.startsWith('temp_') && tempIdMap[tempKey]) {
        contactId = tempIdMap[tempKey];
      }
      await contactsApi.update(
        contactId,
        payload.updates as Parameters<typeof contactsApi.update>[1],
      );
      break;
    }

    case 'deleteContact': {
      let contactId = payload.id as number;
      const tempKey = String(contactId);
      if (tempKey.startsWith('temp_') && tempIdMap[tempKey]) {
        contactId = tempIdMap[tempKey];
      }
      await contactsApi.delete(contactId);
      break;
    }

    case 'addInteraction': {
      const data = payload as Record<string, unknown>;
      let contactId = data.contact_id as number;
      // Resolve temp contact ID if needed
      if (item.dependsOnTempId && tempIdMap[item.dependsOnTempId]) {
        contactId = tempIdMap[item.dependsOnTempId];
      }

      // Convert base64 attachments back to File objects for upload
      let attachments: File[] | undefined;
      const base64List = data.base64Attachments as string[] | undefined;
      if (base64List && base64List.length > 0) {
        attachments = base64List.map((b64, i) => {
          const [header, content] = b64.split(',');
          const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
          const bytes = atob(content);
          const arr = new Uint8Array(bytes.length);
          for (let j = 0; j < bytes.length; j++) arr[j] = bytes.charCodeAt(j);
          return new File([arr], `offline-photo-${i}.${mime.split('/')[1]}`, { type: mime });
        });
      }

      // Strip base64Attachments from payload before sending
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { base64Attachments: _b64, ...apiData } = data;
      await interactionsApi.create({
        ...(apiData as Parameters<typeof interactionsApi.create>[0]),
        contact_id: contactId,
        attachments,
      });
      break;
    }

    case 'deleteInteraction': {
      await interactionsApi.delete(payload.id as number);
      break;
    }

    case 'updateProfile': {
      await authApi.updateProfile(payload as Parameters<typeof authApi.updateProfile>[0]);
      break;
    }
  }
}

// Check if there are queued items and replay on startup
export async function replayOnMount(callbacks: ReplayCallbacks): Promise<void> {
  if (!navigator.onLine) return;
  const length = await getQueueLength();
  if (length > 0) {
    callbacks.onQueueLengthChange(length);
    await replayQueue(callbacks);
  }
}
